import type { MessagingProtectionSection, ProtectionConnectionChange, ProtectionUpdates } from '@/types/Components/MessagingProtection'
import type { Ref } from 'vue'
import { onBeforeUnmount, onMounted, ref } from 'vue'

export const useMessagingProtectionUpdates = (
  section: MessagingProtectionSection,
  load: () => Promise<void>,
): ProtectionUpdates => {
  const connected: Ref<boolean> = ref(false)
  const channelName = `App.MessagingProtection.${section}`
  let timer: ReturnType<typeof setTimeout> | undefined
  let running: Promise<void> | null = null
  let dirty = false
  let disposed = false

  const scheduleRefresh = (): void => {
    dirty = true
    if (disposed || timer || running) return
    timer = setTimeout(() => {
      timer = undefined
      void refresh()
    }, 500)
  }

  const refresh = async (): Promise<void> => {
    if (disposed) return
    if (running) {
      dirty = true
      return running
    }
    clearTimeout(timer)
    timer = undefined
    dirty = false
    running = load()
    try {
      await running
    } finally {
      running = null
      if (dirty) scheduleRefresh()
    }
  }

  const markDisconnected = (): void => { connected.value = false }
  const connectionChanged = (state: ProtectionConnectionChange): void => {
    if (state.current !== 'connected') markDisconnected()
  }
  const becameVisible = (): void => {
    if (!document.hidden) void refresh()
  }
  const connection = window.Echo?.connector?.pusher?.connection

  onMounted(() => {
    void refresh()
    document.addEventListener('visibilitychange', becameVisible)
    connection?.bind('state_change', connectionChanged)
    try {
      window.Echo.private(channelName)
        .listen('.messaging-protection-updated', scheduleRefresh)
        .subscribed(() => {
          connected.value = true
          scheduleRefresh()
        })
        .error(markDisconnected)
    } catch {
      markDisconnected()
    }
  })

  onBeforeUnmount(() => {
    disposed = true
    clearTimeout(timer)
    connection?.unbind('state_change', connectionChanged)
    document.removeEventListener('visibilitychange', becameVisible)
    window.Echo?.leave(channelName)
  })

  return { connected, refresh }
}
